import { useState, useRef, useEffect } from "react";
import {
  Settings,
  Bell,
  HelpCircle,
  LogOut,
  ChevronRight,
  Award,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import ProfileService, {
  ProcessedProfileData,
} from "../../services/ProfileService";
import medicationService, { LocalMedication } from "../../services/MedicationService";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import InfoSheet from "../../components/common/InfoSheet";
import { Skeleton } from "../../components/ui/skeleton";
// import BridgeResponseDialog from "../../components/BridgeResponseDialog"; // Logout bridge debug UI disabled
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { clearGluceraStorage } from "../../utils/storage";
import { pressLogoutFromFlutter } from "../../utils/flutterHealthBridge";
import AuthService from "@/services/APIs/AuthService";

const ProfilePage = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<ProcessedProfileData | null>(
    null
  );
  const [medications, setMedications] = useState<LocalMedication[]>([]);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(
    localStorage.getItem("profileImage")
  );
  const [bio, setBio] = useState(localStorage.getItem("profile_bio") || "");
  const [showImageModal, setShowImageModal] = useState(false);
  // Logout bridge debug popup — UI disabled, functional logout kept intact.
  // const [showLogoutBridgeDialog, setShowLogoutBridgeDialog] = useState(false);
  // const [isLoggingOut, setIsLoggingOut] = useState(false);
  // const [logoutBridgeData, setLogoutBridgeData] = useState<unknown>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // 1) Call backend to permanently delete the account
      const result = await ProfileService.deleteAccount();

      if (!result.success) {
        message.error(result.message ?? "Failed to delete account.");
        setIsDeleting(false);
        return;
      }

      // 2) Ask Flutter (if inside WebView) to clear its cookies/storage.
      //    Same handler used by regular logout — harmless in a normal browser.
      await pressLogoutFromFlutter();

      // 3) Clear web-side storage identical to the logout flow.
      clearWebSession();

      message.success("Your account has been deleted.");

      // 4) Redirect to the onboarding welcome/login screen.
      window.setTimeout(() => {
        window.location.href = "/glucera/onboardingLoginpage";
      }, 1500);
    } catch (_err) {
      message.error("Something went wrong. Please try again.");
      setIsDeleting(false);
    }
  };



  // Function to refresh profile data
  const _refreshProfile = async () => {
    try {
      setLoading(true);
      const profileData = await ProfileService.getUserProfile();
      setUserProfile(profileData);

      if (profileData.photo) {
        setProfileImage(profileData.photo);
      }
    } catch (_error) {
      // Error handled by ProfileService
    } finally {
      setLoading(false);
    }
  };

  // Fetch profile data from API
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const profileData = await ProfileService.getUserProfile();
        setUserProfile(profileData);

        // Set profile image if available
        if (profileData.photo) {
          setProfileImage(profileData.photo);
        }

        // Fetch medications from CRUD API
        try {
          const meds = await medicationService.getAllMedications();
          setMedications(meds ?? []);
        } catch {
          setMedications([]);
        }
      } catch (_error) {
        // Error handled by ProfileService
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();

    // Listen for storage changes (when preferences are updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "user_preferences") {
        fetchProfile(); // Refresh profile data with new preferences
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Listen for page visibility change (when user comes back to this page)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchProfile();
        setBio(localStorage.getItem("profile_bio") || "");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Listen for focus event (when navigating back to this page)
    const handleFocus = () => { };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const clearWebSession = () => {
    clearGluceraStorage();

    localStorage.removeItem("glucera_refresh_token");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("onboardingComplete");
    localStorage.removeItem("profileImage");
    localStorage.removeItem("cached_meals");
    localStorage.removeItem("user_preferences");
    localStorage.removeItem("assessment_data");
    localStorage.removeItem("onboarding_completed");
    localStorage.removeItem("glucera_document_id");
    localStorage.removeItem("glucera_onboarding_complete");
    localStorage.removeItem("glucera_google_health_cache");

    localStorage.removeItem("accessToken");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("refreshToken");

    sessionStorage.clear();
  };

  const handleLogout = async () => {
    // Debug popup UI intentionally disabled — logout still runs end-to-end.
    // setShowLogoutBridgeDialog(true);
    // setIsLoggingOut(true);
    // setLogoutBridgeData(null);

    // 1) Server: invalidate the refresh token before local storage is cleared.
    try {
      await AuthService.logout();
    } catch {
      // Still sign out on this device if the logout API is unreachable.
    }

    // 2) Flutter: cookies + WebView storage clear (pressLogout handler)
    await pressLogoutFromFlutter();
    // 3) Web: localStorage / sessionStorage clear (same session keys)
    clearWebSession();

    // setLogoutBridgeData({
    //   handler: "pressLogout",
    //   flutterSkipped: flutterResult.skipped,
    //   flutterOk: flutterResult.ok,
    //   flutterResponse: flutterResult.raw,
    //   webStorageCleared: true,
    //   note: "Both sides clear — Flutter pressLogout + web localStorage",
    // });
    // setIsLoggingOut(false);
    message.success("You have been logged out successfully!");

    window.setTimeout(() => {
      window.location.href = "/glucera/onboardingLoginpage";
    }, 2000);
  };

  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          message.error("Image size should be less than 5MB");
          return;
        }

        // Upload using ProfileService
        const imageUrl = await ProfileService.uploadProfileImage(file);

        if (imageUrl) {
          setProfileImage(imageUrl);
          // message.success("Profile photo updated successfully!");
        } else {
          message.error("Failed to upload image. Please try again.");
        }
      } catch (_error) {
        message.error("Failed to upload image. Please try again.");
      }
    }
  };

  const menuSections = [
    // {
    //   title: "Account",
    //   items: [
    //     {
    //       icon: Activity,
    //       label: "Connected Devices",
    //       color: "text-chart-3",
    //       bgColor: "bg-accent-light",
    //     },
    //   ],
    // },
    {
      title: "App Settings",
      items: [
        {
          icon: Bell,
          label: "Notifications",
          color: "text-chart-4",
          bgColor: "bg-info-light",
        },
        {
          icon: Settings,
          label: "Settings",
          color: "text-muted-foreground",
          bgColor: "bg-muted",
        },
        // {
        //   icon: Shield,
        //   label: "Privacy & Security",
        //   color: "text-muted-foreground",
        //   bgColor: "bg-muted",
        // },
      ],
    },
    {
      title: "Support",
      items: [
        {
          icon: HelpCircle,
          label: "Help & Support",
          color: "text-muted-foreground",
          bgColor: "bg-muted",
        },
        {
          icon: Award,
          label: "About Bite Balance",
          color: "text-muted-foreground",
          bgColor: "bg-muted",
        },
      ],
    },
  ];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="pt-2">
          <h2 className="text-sm font-medium text-muted-foreground">My Account</h2>
          <h1 className="text-2xl font-semibold text-foreground">Profile</h1>
          <p className="mt-0.5 text-caption1 text-muted-foreground">
            Manage your account & preferences
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {/* Profile card skeleton */}
            <div className="rounded-2xl border border-border bg-card p-5 shadow-lg space-y-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-36 rounded-md" />
                  <Skeleton className="h-4 w-48 rounded-md" />
                </div>
              </div>
              <Skeleton className="h-9 w-full rounded-lg" />
            </div>
            {/* Stats skeleton */}
            <div className="grid grid-cols-3 gap-3">
              <Skeleton className="h-24 rounded-2xl" />
              <Skeleton className="h-24 rounded-2xl" />
              <Skeleton className="h-24 rounded-2xl" />
            </div>
            {/* BMI skeleton */}
            <Skeleton className="h-36 rounded-xl" />
            {/* Goals skeleton */}
            <Skeleton className="h-48 rounded-2xl" />
            {/* Menu skeleton */}
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-28 rounded-2xl" />
          </div>
        )}

        {/* Profile Content - Only show when not loading and data is available */}
        {!loading && userProfile && (
          <>
            {/* User Profile Card */}
            <div
              className="cursor-pointer rounded-2xl border border-border bg-card p-5 shadow-lg transition-shadow hover:shadow-xl"
              onClick={() => navigate("/glucera" + GluceraNavigationRoutes.EDIT_PROFILE)}
            >
              <div className="mb-4 flex items-center gap-4">
                <div className="relative">
                  <div
                    className="h-16 w-16 cursor-pointer overflow-hidden rounded-full border-4 border-blue-100 bg-gradient-to-br from-primary to-chart-1 transition-opacity hover:opacity-80 dark:border-blue-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (profileImage) setShowImageModal(true);
                      else handleImageUpload();
                    }}
                  >
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xl font-bold text-primary-foreground">
                        {userProfile.name
                          ?.split(" ")
                          .map((n: string) => n[0])
                          .join("") || "HA"}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-foreground truncate">
                    {userProfile.name}
                  </h3>
                  <p className="text-sm text-muted-foreground break-all">
                    {userProfile.email}
                  </p>
                  {bio && (
                    <p className="mt-1 text-xs text-muted-foreground/80 line-clamp-2">
                      {bio}
                    </p>
                  )}
                </div>
              </div>

              <button
                className="h-9 w-full rounded-lg border border-border bg-secondary text-sm font-medium text-secondary-foreground transition-all duration-200 hover:bg-secondary/80 hover:shadow-sm"
                onClick={() =>
                  navigate("/glucera" + GluceraNavigationRoutes.EDIT_PROFILE)
                }
              >
                Edit Profile
              </button>
            </div>

            {/* Stats Overview - Grid Layout for Uniform Cards */}
            <div className="grid grid-cols-3 gap-3">
              {/* Weight Card */}
              <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card px-1.5 py-3.5 shadow-md">
                <div className="flex items-baseline gap-0.5">
                  <span className="text-callout xs:text-body font-black leading-tight text-foreground">
                    {Number(userProfile.weight).toFixed(1)}
                  </span>
                  <span className="text-footnote xs:text-subhead font-black text-foreground">
                    {userProfile.weightUnit}
                  </span>
                </div>
                <div className="mt-1 text-caption2 xs:text-caption1 font-extrabold uppercase tracking-wide text-foreground/60">
                  Weight
                </div>
                <div className="mt-1 flex min-h-5 items-center justify-center">
                  {Number(userProfile.targetWeight) > 0 ? (
                    <div className="whitespace-nowrap text-compact-xs xs:text-compact-sm font-semibold text-blue-600 dark:text-blue-400">
                      Target: {Number(userProfile.targetWeight).toFixed(1)}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Height Card */}
              <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card px-1.5 py-3.5 shadow-md">
                <div className="flex items-baseline gap-0.5">
                  {userProfile.heightUnit === "cm" ? (
                    <>
                      <span className="text-callout xs:text-body font-black leading-tight text-foreground">
                        {userProfile.heightCm}
                      </span>
                      <span className="text-footnote xs:text-subhead font-black text-foreground">cm</span>
                    </>
                  ) : (
                    <span className="text-callout xs:text-body font-black leading-tight text-foreground">
                      {userProfile.height}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-caption2 xs:text-caption1 font-extrabold uppercase tracking-wide text-foreground/60">
                  Height
                </div>
                <div className="mt-1 flex min-h-5 items-center justify-center">
                  <div className="whitespace-nowrap text-compact-xs xs:text-compact-sm font-semibold text-green-600 dark:text-green-400">
                    {userProfile.gender}
                  </div>
                </div>
              </div>

              {/* Age Card */}
              <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card px-1.5 py-3.5 shadow-md">
                <div className="flex items-baseline gap-0.5">
                  <span className="text-callout xs:text-body font-black leading-tight text-foreground">
                    {userProfile.age}
                  </span>
                  <span className="text-footnote xs:text-subhead font-black text-foreground">Yrs</span>
                </div>
                <div className="mt-1 text-caption2 xs:text-caption1 font-extrabold uppercase tracking-wide text-foreground/60">
                  Age
                </div>
                <div className="mt-1 h-4" />
              </div>
            </div>



            {/* BMI Card */}
            {(() => {
              const { bmi, category, color, heightCm, weightKg } = userProfile.bmiData || { bmi: 0, category: 'Unknown', color: 'gray', heightCm: 0, weightKg: 0 };
              return (
                <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-base font-bold text-foreground">
                      Body Mass Index (BMI)
                    </h3>
                    <InfoSheet title="Body Mass Index (BMI)" ariaLabel="About BMI">
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-sm font-bold text-foreground">How BMI is Calculated</h4>
                          <p className="mt-1 text-xs font-semibold text-muted-foreground">
                            BMI = weight (kg) / height² (m²)
                          </p>
                        </div>
                        <div className="space-y-2 text-xs">
                          <p className="font-bold text-foreground">BMI Categories:</p>
                          <div className="space-y-1 font-semibold text-muted-foreground">
                            <p>• Underweight: &lt; 18.5</p>
                            <p>• Normal weight: 18.5 - 24.9</p>
                            <p>• Overweight: 25 - 29.9</p>
                            <p>• Obese: ≥ 30</p>
                          </div>
                        </div>
                      </div>
                    </InfoSheet>
                  </div>
                  <div className="flex items-center justify-center">
                    <div className="text-center">
                      <div className="mb-2 text-5xl font-bold text-blue-500">
                        {bmi?.toFixed(1) || "0"}
                      </div>
                      <div
                        className="inline-block rounded-xl border-2 px-4 py-2"
                        style={{
                          borderColor:
                            color === "red"
                              ? "rgba(220, 38, 38, 0.3)"
                              : color === "yellow"
                                ? "rgba(202, 138, 4, 0.3)"
                                : color === "orange"
                                  ? "rgba(234, 88, 12, 0.3)"
                                  : "rgba(22, 163, 74, 0.3)",
                          backgroundColor:
                            color === "red"
                              ? "rgba(220, 38, 38, 0.1)"
                              : color === "yellow"
                                ? "rgba(202, 138, 4, 0.1)"
                                : color === "orange"
                                  ? "rgba(234, 88, 12, 0.1)"
                                  : "rgba(22, 163, 74, 0.1)",
                        }}
                      >
                        <p
                          className="text-sm font-bold"
                          style={{
                            color:
                              color === "red"
                                ? "#dc2626"
                                : color === "yellow"
                                  ? "#ca8a04"
                                  : color === "orange"
                                    ? "#ea580c"
                                    : "#16a34a",
                          }}
                        >
                          {category}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })()}

            {/* Goals & Preferences Card */}
            <div
              className="cursor-pointer rounded-2xl border border-border bg-card p-5 shadow-lg transition-shadow hover:shadow-xl"
              onClick={() =>
                navigate("/glucera" + GluceraNavigationRoutes.PROFILE_GOALS)
              }
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-callout font-semibold text-foreground">
                  Goals & Preferences
                </h3>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>

              <div className="space-y-3">
                {/* Weight Loss Goal */}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">
                    Weight Loss Goal
                  </span>
                  <span className="max-w-[55%] truncate text-right font-medium capitalize text-foreground">
                    {userProfile.weightLossGoal}
                  </span>
                </div>

                <div className="h-px bg-border" />

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">
                    Activity Level
                  </span>
                  <span className="max-w-[55%] truncate text-right font-medium capitalize text-foreground">
                    {userProfile.activityLevel}
                  </span>
                </div>
                <div className="h-px bg-border" />

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">
                    Diet Type
                  </span>
                  <span className="max-w-[55%] truncate text-right font-medium text-foreground">
                    {userProfile.dietType}
                  </span>
                </div>
                <div className="h-px bg-border" />

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">
                    Fasting Window
                  </span>
                  <span className="max-w-[55%] truncate text-right font-medium text-foreground">
                    {userProfile.fastingWindow}
                  </span>
                </div>

                {/* Medications Section */}
                {medications && medications.length > 0 && (
                  <>
                    {medications.slice(0, 2).map((med, idx) => {
                      const formatTime = (t: string) => {
                        if (!t) return "-";
                        const [hStr, mStr] = t.split(":");
                        const h = parseInt(hStr, 10);
                        const m = mStr ?? "00";
                        const period = h >= 12 ? "PM" : "AM";
                        return `${String(h % 12 || 12).padStart(2, "0")}:${m} ${period}`;
                      };
                      return (
                        <div key={idx}>
                          <div className="h-px bg-border" />
                          <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-muted-foreground">
                              {med.medication_name}
                            </span>
                            <span className="max-w-[55%] truncate text-right text-xs font-medium text-foreground">
                              {formatTime(med.time)} • {med.frequency}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {medications.length > 2 && (
                      <>
                        <div className="h-px bg-border" />
                        <div className="flex items-center justify-between py-2">
                          <span className="text-sm text-muted-foreground">
                            More Medications
                          </span>
                          <span className="text-xs font-medium text-foreground">
                            +{medications.length - 2} more
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Menu Sections */}
            {menuSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="space-y-2">
                <h4 className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.title}
                </h4>
                <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
                  {section.items.map((item, index) => {
                    const Icon = item.icon;
                    const routes: Record<string, string> = {
                      "Personal Information": "/profile/personal-info",
                      "Goals & Preferences": "/profile/goals",
                      // "Connected Devices": "/profile/devices",
                      Notifications: "/profile/notifications",
                      Settings: "/profile/preferences",
                      "Privacy & Security": "/profile/privacy",
                      "Help & Support": "/profile/help",
                      "About Bite Balance": "/profile/about",
                    };
                    return (
                      <div key={index}>
                        <button
                          className="flex w-full items-center gap-4 p-4 transition-colors hover:bg-muted/50"
                          onClick={() => {
                            // Handle profile navigation routes
                            if (item.label === "Connected Devices") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_DEVICES
                              );
                            } else if (item.label === "Notifications") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.NOTIFICATIONS
                              );
                            } else if (item.label === "Settings") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_PREFERENCES
                              );
                            } else if (item.label === "Privacy & Security") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_PRIVACY
                              );
                            } else if (item.label === "Help & Support") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_HELP
                              );
                            } else if (
                              item.label === "About Bite Balance"
                            ) {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_ABOUT
                              );
                            } else {
                              const route = routes[item.label];
                              if (route) {
                                navigate(route);
                              }
                            }
                          }}
                        >
                          <div
                            className={`h-10 w-10 rounded-xl ${item.bgColor} flex items-center justify-center`}
                          >
                            <Icon className={`h-5 w-5 ${item.color}`} />
                          </div>
                          <span className="flex-1 text-left text-base font-medium text-foreground">
                            {item.label}
                          </span>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </button>
                        {index < section.items.length - 1 && (
                          <div className="mx-4 h-px bg-border" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex h-12 w-full items-center justify-center rounded-2xl border border-red-300 bg-card text-base font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="mr-2 h-5 w-5" />
              Logout
            </button>

            {/* Delete Account Button */}
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="flex h-12 w-full items-center justify-center rounded-2xl border border-red-500 bg-red-600 text-base font-medium text-white transition-colors hover:bg-red-700"
            >
              <Trash2 className="mr-2 h-5 w-5" />
              Delete Account
            </button>

            {/* App Version */}
            <p className="text-center text-xs text-muted-foreground">
              Bite Balance v1.0.0
            </p>
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Logout Bridge debug popup — UI disabled. Uncomment to re-enable.
      <BridgeResponseDialog
        open={showLogoutBridgeDialog}
        title="Logout Bridge"
        subtitle="Flutter → Web response"
        loading={isLoggingOut}
        loadingText="Calling pressLogout..."
        data={logoutBridgeData}
        onClose={() => setShowLogoutBridgeDialog(false)}
      /> */}

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          // Prevent closing the dialog mid-request
          if (isDeleting) return;
          setShowDeleteDialog(open);
        }}
      >
        <DialogContent className="max-w-[90vw] rounded-2xl sm:max-w-md">
          <DialogHeader>
            <div className="mb-2 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <DialogTitle>Delete Account?</DialogTitle>
            </div>
            <DialogDescription className="text-left leading-relaxed">
              This will permanently delete your Bite Balance account along with
              all your meals, glucose logs, fitness data, medications, and
              preferences. This action <strong>cannot be undone</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className="rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-70"
            >
              {isDeleting ? "Deleting..." : "Yes, Delete My Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Image Lightbox Modal */}
      {showImageModal && profileImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="relative mx-4 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={profileImage}
              alt="Profile"
              className="max-h-[75vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
            />
            <button
              className="mt-4 rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white backdrop-blur-sm hover:bg-white/30"
              onClick={() => setShowImageModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <BottomNavBar activeIndex={3} />
    </div>
  );
};

export default ProfilePage;
